import {QuestionCircleOutlined} from '@ant-design/icons';
import type {TableProps} from 'antd';
import {Tooltip, Typography} from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import {AdminListingCheck, AdminListingCheckStatus, AdminMarketComparable} from '../../../../gen/api/models';
import {formatAgo, formatPrice, parseMoney} from '../helpers/slowSelling';

const {Text} = Typography;

export const TAG_COLOR = {error: 'red', warning: 'orange', muted: 'default', success: 'green'} as const;

export const empty = <Text type="secondary">—</Text>;

/** A column or field label with a "?" icon that explains it on hover. */
export const Hint: React.FC<{label: React.ReactNode; hint: React.ReactNode}> = ({label, hint}) => (
    <Tooltip title={hint}>
        <span style={{cursor: 'help', whiteSpace: 'nowrap'}}>
            {label} <QuestionCircleOutlined style={{fontSize: 11, opacity: 0.45}} />
        </span>
    </Tooltip>
);

export const CHECK_PROBLEM: Partial<Record<AdminListingCheckStatus, string>> = {
    [AdminListingCheckStatus.ListingCheckEnded]: 'Ended on marketplace',
    [AdminListingCheckStatus.ListingCheckNotFound]: 'Not found on marketplace',
    [AdminListingCheckStatus.ListingCheckUnresolved]: 'Link not recognized',
    [AdminListingCheckStatus.ListingCheckNotOurs]: "Link is another seller's item",
    [AdminListingCheckStatus.ListingCheckError]: 'Check failed',
};

/** The latest check of our listing on its marketplace; renders nothing when there is none. */
export const ListingCheckNote: React.FC<{check?: AdminListingCheck | null}> = ({check}) => {
    if (!check?.status) {
        return null;
    }
    const problem = CHECK_PROBLEM[check.status];
    const checked = `checked ${formatAgo(check.checkedAt)}`;
    const tooltip = (
        <>
            Checked on the marketplace {dayjs(check.checkedAt).format('MM/DD/YYYY HH:mm')}
            {check.price !== undefined && check.price !== null ? (
                <div>Price there: {formatPrice(check.price)}</div>
            ) : null}
        </>
    );
    return (
        <Tooltip title={tooltip}>
            <div style={{fontSize: 12, lineHeight: 1.3, cursor: 'help'}}>
                {problem ? (
                    <Text type="danger">{problem}</Text>
                ) : (
                    check.condition && <Text type="secondary">{check.condition}</Text>
                )}
                <div>
                    <Text type="secondary">{checked}</Text>
                </div>
            </div>
        </Tooltip>
    );
};

export const comparableColumns: TableProps<AdminMarketComparable>['columns'] = [
    {
        title: '',
        dataIndex: 'imageUrl',
        key: 'image',
        width: 48,
        render: (url?: string) =>
            url ? (
                <img
                    src={url}
                    alt=""
                    style={{width: 36, height: 36, objectFit: 'cover', borderRadius: 4}}
                />
            ) : null,
    },
    {
        title: (
            <Hint
                label="Comparable"
                hint="Active eBay listings of the same model (fixed price, ships to the US, not ours). Parts, other models and outliers are dropped."
            />
        ),
        key: 'title',
        render: (_, c) => (
            <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
            >
                {c.title}
            </a>
        ),
    },
    {
        title: 'Condition',
        dataIndex: 'condition',
        key: 'condition',
        width: 110,
        render: (value?: string) => value || empty,
    },
    {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        width: 90,
        render: (value) => formatPrice(value),
    },
    {
        title: (
            <Hint
                label="Shipping"
                hint="What the buyer pays for shipping. ~ means eBay did not give the cost (calculated shipping), so our average label cost is used."
            />
        ),
        key: 'shipping',
        width: 100,
        render: (_, c) =>
            c.shippingEstimated ? (
                <Tooltip title="Shipping cost unknown; our average label cost is used">
                    <Text type="secondary">~{formatPrice(c.shipping)}</Text>
                </Tooltip>
            ) : parseMoney(c.shipping) ? (
                formatPrice(c.shipping)
            ) : (
                <Text type="secondary">free</Text>
            ),
    },
    {
        title: (
            <Hint
                label="Total"
                hint="Price + shipping: what the buyer pays. The market median is taken from these totals."
            />
        ),
        dataIndex: 'total',
        key: 'total',
        width: 90,
        render: (value) => <Text strong>{formatPrice(value)}</Text>,
    },
];
